import { useState } from 'react';
import { Loader2, AlertTriangle, Building2, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const STATUS_LABEL = {
  no_credential: 'No credential',
  auth_error: 'Auth error',
  request_failed: 'Request failed',
  no_listings: 'No listings found',
  ok: 'OK',
};

const STATUS_VARIANT = {
  no_credential: 'outline',
  auth_error: 'destructive',
  request_failed: 'destructive',
  no_listings: 'outline',
  ok: 'default',
};

export function HostawayDiscoveryPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runDiscover = async () => {
    setLoading(true);
    try {
      const { data } = await diagnosticsAPI.hostawayDiscovery();
      setResult(data);
      if (data.status === 'ok') {
        toast.success('Hostaway discovery succeeded');
      } else {
        toast.error(data.error || data.status);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="hostaway-discovery-panel">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Hostaway Discovery
          </CardTitle>
          {result && (
            <Badge variant={STATUS_VARIANT[result.status] || 'outline'} data-testid="hostaway-discovery-status">
              {STATUS_LABEL[result.status] || result.status}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Read-only. Exchanges the stored/env Hostaway credentials for a bearer token,
          lists connected listings, and pulls a raw sample of the 5 most recent
          reservations exactly as Hostaway returns them (guest name/email/phone
          redacted). Use this to confirm the real amount format, sign convention,
          currency field, reservation id, and channel values before trusting any
          downstream normalization.
        </p>

        <Button size="sm" variant="outline" onClick={runDiscover} disabled={loading} data-testid="hostaway-discovery-run">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Search className="h-3.5 w-3.5 mr-1.5" />}
          Run discovery
        </Button>

        {result && result.error && (
          <div className="flex items-center gap-2 text-sm text-red-600">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {result.error}
          </div>
        )}

        {result && result.status === 'no_listings' && (
          <p className="text-sm text-muted-foreground">
            Credential connected, but Hostaway returned zero listings for this account.
          </p>
        )}

        {result && result.listings && result.listings.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">
              {result.listings.length} listing(s) discovered:
            </p>
            <div className="space-y-1">
              {result.listings.map((listing) => (
                <div key={listing.id} className="rounded border px-3 py-2 flex items-center gap-2 text-sm">
                  <span className="font-medium">{listing.name || '(unnamed)'}</span>
                  <span className="text-xs text-muted-foreground">{listing.currency}</span>
                  <code className="text-[10px] text-muted-foreground ml-auto">id={listing.id}</code>
                </div>
              ))}
            </div>
          </div>
        )}

        {result && result.reservations_error && (
          <div className="flex items-center gap-2 text-sm text-red-600">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {result.reservations_error}
          </div>
        )}

        {result && result.reservations_sample && result.reservations_sample.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">
              {result.reservations_sample.length} most recent reservation(s), exactly as
              returned by the Hostaway API:
            </p>
            <pre
              className="text-[10px] border rounded p-2 bg-muted/30 overflow-x-auto max-h-96 overflow-y-auto"
              data-testid="hostaway-discovery-reservations-sample"
            >
              {JSON.stringify(result.reservations_sample, null, 2)}
            </pre>
          </div>
        )}

        {result && result.status === 'ok' && result.reservations_sample
          && result.reservations_sample.length === 0 && !result.reservations_error && (
          <p className="text-sm text-muted-foreground" data-testid="hostaway-discovery-zero-reservations">
            Query succeeded, but Hostaway returned zero reservations for this account.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
